import { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../lib/auth.js';
import { api } from '../lib/api.js';
import { Modal } from '../components/Modal.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { TableLoader } from '../components/TableLoader.js';
import { TimestampCell } from '../components/TimestampCell.js';
import {
  ALL_APP_SECTIONS,
  SECTION_LABELS,
  getDefaultPermissions,
  type AppRole,
  type AppSection,
  type AppUser,
  type PermissionLevel,
  type SectionPermissions,
} from '@gng-ehs/shared';

const SUPER_ADMIN_EMAIL = 'rohan@gandg.com';

const ROLE_BADGES: Record<AppRole, { label: string; bg: string }> = {
  admin: { label: 'Admin', bg: 'bg-purple-50 text-purple-700 border-purple-200' },
  read_write: { label: 'Read / Write', bg: 'bg-blue-50 text-blue-700 border-blue-200' },
  read_only: { label: 'Read Only', bg: 'bg-slate-50 text-slate-700 border-slate-200' },
  custom: { label: 'Custom', bg: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

export function AdminManagementPage() {
  const { user, isAdmin } = useAuth();
  const [users, setUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');

  // Modals
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [selectedUser, setSelectedUser] = useState<AppUser | null>(null);
  const [deleteUserItem, setDeleteUserItem] = useState<AppUser | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  // Form Fields
  const [formEmail, setFormEmail] = useState('');
  const [formFullName, setFormFullName] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [formRole, setFormRole] = useState<AppRole>('read_write');
  const [formPermissions, setFormPermissions] = useState<SectionPermissions>(getDefaultPermissions('read_write'));
  const [formStatus, setFormStatus] = useState<'active' | 'disabled'>('active');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.users.list();
      setUsers(res.data || []);
    } catch (err: any) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      load();
    }
  }, [isAdmin]);

  const filteredUsers = useMemo(() => {
    if (!search) return users;
    const q = search.toLowerCase().trim();
    return users.filter(
      (u) =>
        u.email.toLowerCase().includes(q) ||
        (u.full_name && u.full_name.toLowerCase().includes(q)) ||
        u.role.toLowerCase().includes(q)
    );
  }, [users, search]);

  const openCreate = () => {
    setModalMode('create');
    setSelectedUser(null);
    setFormEmail('');
    setFormFullName('');
    setFormPassword('');
    setFormRole('read_write');
    setFormPermissions(getDefaultPermissions('read_write'));
    setFormStatus('active');
    setFormError('');
    setModalOpen(true);
  };

  const openEdit = (u: AppUser) => {
    setModalMode('edit');
    setSelectedUser(u);
    setFormEmail(u.email);
    setFormFullName(u.full_name || '');
    setFormPassword('');
    setFormRole(u.role);
    setFormPermissions(u.permissions || getDefaultPermissions(u.role));
    setFormStatus(u.status);
    setFormError('');
    setModalOpen(true);
  };

  const handleRoleChange = (newRole: AppRole) => {
    setFormRole(newRole);
    if (newRole !== 'custom') {
      setFormPermissions(getDefaultPermissions(newRole));
    }
  };

  const handlePermissionChange = (section: AppSection, level: PermissionLevel) => {
    setFormPermissions((prev) => ({
      ...prev,
      [section]: level,
    }));
    setFormRole('custom');
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setFormError('');

    try {
      if (modalMode === 'create') {
        if (!formEmail.trim() || !formPassword) {
          setFormError('Email and initial password are required.');
          setSaving(false);
          return;
        }
        if (formPassword.length < 6) {
          setFormError('Password must be at least 6 characters.');
          setSaving(false);
          return;
        }

        await api.users.create({
          email: formEmail.trim(),
          full_name: formFullName.trim() || null,
          password: formPassword,
          role: formRole,
          permissions: formPermissions,
          status: formStatus,
        });

        setSuccess(`User ${formEmail} created successfully.`);
      } else if (modalMode === 'edit' && selectedUser) {
        await api.users.update(selectedUser.id, {
          full_name: formFullName.trim() || null,
          role: formRole,
          permissions: formPermissions,
          status: formStatus,
        });

        setSuccess(`User ${selectedUser.email} updated successfully.`);
      }

      setModalOpen(false);
      load();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err: any) {
      setFormError(err instanceof Error ? err.message : 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteUserItem) return;
    try {
      await api.users.delete(deleteUserItem.id);
      setSuccess(`User ${deleteUserItem.email} deleted.`);
      setDeleteUserItem(null);
      load();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err: any) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex-1 flex items-center justify-center p-6 text-center">
        <div className="card p-8 max-w-md w-full text-center space-y-3">
          <span className="text-4xl">🔒</span>
          <h2 className="text-lg font-bold text-gray-900">Access Restricted</h2>
          <p className="text-sm text-gray-500">
            You do not have administrator permissions to access User Management. Please contact <strong>{SUPER_ADMIN_EMAIL}</strong>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0 space-y-4 overflow-hidden pt-1 px-1">
      {/* ── Toolbar ─────────────────── */}
      <div className="shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-1">
        <div className="relative w-full sm:w-80">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Search users by email, name, role…"
            className="input pl-9 text-sm w-full"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button onClick={load} className="btn btn-secondary">Refresh</button>
          <button onClick={openCreate} className="btn btn-primary">+ Add New User</button>
        </div>
      </div>

      {/* ── Alerts ─────────────────────── */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-2 text-xs font-semibold text-red-700 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 text-xs">Dismiss</button>
        </div>
      )}
      {success && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-2 text-xs font-semibold text-emerald-700 flex items-center justify-between">
          <span>{success}</span>
          <button onClick={() => setSuccess('')} className="text-emerald-500 hover:text-emerald-700 text-xs">Dismiss</button>
        </div>
      )}

      {/* ── Table ───────────────────────── */}
      {loading ? (
        <TableLoader />
      ) : (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden space-y-2">
          <div className="table-wrapper">
            <table className="table">
              <thead className="sticky top-0 z-10 bg-white">
                <tr>
                  <th>User Details</th>
                  <th>Role</th>
                  <th>Section Permissions</th>
                  <th>Status</th>
                  <th>Created / Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center text-gray-500 py-8">
                      No users found
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((u) => {
                    const isSuper = u.email.toLowerCase() === SUPER_ADMIN_EMAIL;
                    const badge = ROLE_BADGES[u.role] || ROLE_BADGES.read_write;
                    return (
                      <tr key={u.id}>
                        <td>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-brand-600 text-white font-bold flex items-center justify-center text-xs flex-shrink-0">
                              {u.email[0].toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="font-semibold text-gray-900 text-xs truncate">
                                  {u.full_name || u.email.split('@')[0]}
                                </span>
                                {isSuper && (
                                  <span className="px-1.5 py-0.2 text-[9px] font-bold uppercase bg-amber-50 text-amber-800 border border-amber-300 rounded">
                                    Super Admin
                                  </span>
                                )}
                              </div>
                              <span className="text-[11px] text-gray-500 truncate block">
                                {u.email}
                              </span>
                            </div>
                          </div>
                        </td>

                        <td>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${badge.bg}`}>
                            {badge.label}
                          </span>
                        </td>

                        <td>
                          <div className="flex flex-wrap gap-1 max-w-xs">
                            {ALL_APP_SECTIONS.filter((s) => s !== 'admin').map((sec) => {
                              const level = u.permissions?.[sec] || (u.role === 'admin' ? 'write' : 'none');
                              if (level === 'none') return null;
                              const isWrite = level === 'write';
                              return (
                                <span
                                  key={sec}
                                  className={`px-1.5 py-0.5 text-[10px] font-medium rounded border ${
                                    isWrite
                                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                      : 'bg-gray-50 text-gray-600 border-gray-200'
                                  }`}
                                >
                                  {SECTION_LABELS[sec]}: {isWrite ? 'W' : 'R'}
                                </span>
                              );
                            })}
                          </div>
                        </td>

                        <td>
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${
                              u.status === 'active'
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                : 'bg-red-50 text-red-700 border border-red-200'
                            }`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${u.status === 'active' ? 'bg-emerald-500' : 'bg-red-500'}`} />
                            {u.status === 'active' ? 'Active' : 'Disabled'}
                          </span>
                        </td>

                        <td className="whitespace-nowrap">
                          <TimestampCell createdAt={u.created_at} updatedAt={u.updated_at} />
                        </td>

                        <td>
                          <div className="flex gap-1.5 items-center">
                            <button
                              onClick={() => openEdit(u)}
                              title="Edit User Permissions"
                              className="p-1.5 rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 hover:text-amber-800 border border-amber-200/80 transition-colors cursor-pointer"
                            >
                              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                            {!isSuper && u.email.toLowerCase() !== user?.email?.toLowerCase() && (
                              <button
                                onClick={() => setDeleteUserItem(u)}
                                title="Delete User"
                                className="p-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-800 border border-red-200/80 transition-colors cursor-pointer"
                              >
                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Create / Edit Modal ─────────── */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalMode === 'create' ? 'Add New User' : 'Edit User Permissions'}
        size="lg"
      >
        <form onSubmit={handleSave} className="space-y-4">
          {formError && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-2 text-xs font-semibold text-red-700">
              {formError}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Email Address <span className="text-red-500">*</span></label>
              <input
                type="email"
                required
                disabled={modalMode === 'edit'}
                value={formEmail}
                onChange={(e) => setFormEmail(e.target.value)}
                placeholder="user@example.com"
                className="input text-sm w-full disabled:bg-gray-100"
              />
            </div>
            <div>
              <label className="label">Full Name</label>
              <input
                type="text"
                value={formFullName}
                onChange={(e) => setFormFullName(e.target.value)}
                placeholder="e.g. Rahul Sharma"
                className="input text-sm w-full"
              />
            </div>
          </div>

          {modalMode === 'create' && (
            <div>
              <label className="label">Initial Password <span className="text-red-500">*</span></label>
              <input
                type="password"
                required
                value={formPassword}
                onChange={(e) => setFormPassword(e.target.value)}
                placeholder="Min 6 characters"
                className="input text-sm w-full"
              />
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Role Preset</label>
              <select
                value={formRole}
                onChange={(e) => handleRoleChange(e.target.value as AppRole)}
                className="input text-sm w-full"
              >
                <option value="admin">Admin (Full Access to Everything)</option>
                <option value="read_write">Read / Write (All Operations)</option>
                <option value="read_only">Read Only (View Access Only)</option>
                <option value="custom">Custom (Granular per section)</option>
              </select>
            </div>
            <div>
              <label className="label">Account Status</label>
              <select
                value={formStatus}
                onChange={(e) => setFormStatus(e.target.value as 'active' | 'disabled')}
                className="input text-sm w-full"
              >
                <option value="active">Active</option>
                <option value="disabled">Disabled (Revoke Access)</option>
              </select>
            </div>
          </div>

          {/* Section Permissions Matrix */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="label mb-0">Section Permissions</label>
              <span className="text-xs text-gray-500">
                {formRole === 'custom' ? 'Customized' : `Preset: ${ROLE_BADGES[formRole]?.label}`}
              </span>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden divide-y divide-gray-100 text-xs">
              {ALL_APP_SECTIONS.map((sec) => {
                const currentVal = formPermissions[sec] || (formRole === 'admin' ? 'write' : 'none');
                return (
                  <div key={sec} className="flex items-center justify-between px-3 py-2 bg-gray-50/50 hover:bg-gray-50">
                    <span className="font-semibold text-gray-800">{SECTION_LABELS[sec]}</span>
                    <div className="flex gap-1">
                      {(['none', 'read', 'write'] as PermissionLevel[]).map((level) => (
                        <button
                          key={level}
                          type="button"
                          onClick={() => handlePermissionChange(sec, level)}
                          className={`px-2 py-0.5 rounded text-[11px] font-semibold border transition-all ${
                            currentVal === level
                              ? level === 'write'
                                ? 'bg-emerald-600 text-white border-emerald-600'
                                : level === 'read'
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'bg-red-600 text-white border-red-600'
                              : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-100'
                          }`}
                        >
                          {level === 'none' ? 'None' : level === 'read' ? 'Read' : 'Read/Write'}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn btn-primary"
            >
              {saving ? 'Saving…' : modalMode === 'create' ? 'Create User' : 'Save Changes'}
            </button>
          </div>
        </form>
      </Modal>

      {/* ── Delete Confirmation ──────────── */}
      <ConfirmDialog
        open={!!deleteUserItem}
        title="Delete User"
        message={`Are you sure you want to delete "${deleteUserItem?.email}"? This will revoke their access to the portal.`}
        confirmLabel="Delete User"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteUserItem(null)}
      />
    </div>
  );
}
